import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retryWhen } from 'rxjs/operators';
import { X_SKIP_ALERT } from '../constants/http-headers';
import { ThErrorResponse } from '../models/th-error-response';
import { GlobalModalService } from '../services/global-modal.service';
import { isObject, isString } from '../utils/type-util';

const REDIS_EXCEPTION = 'org.springframework.data.redis.RedisConnectionFailureException';

/**
 * Provides basic error intercepting for all HTTP requests.
 * Also includes a retry mechanism for requests that meet a certain criteria.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingInterceptor implements HttpInterceptor {
  constructor(
    private globalModalService: GlobalModalService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      retryWhen(this.genericRetryStrategy()),
      catchError((errorResponse) => {
        return this.handleError(errorResponse, req);
      })
    );
  }

  private handleError(errorResponse: any, req: HttpRequest<any>) {
    const showAlert = () => {
      const skipAlert = req.headers.get(X_SKIP_ALERT) === '1';

      // Allow API calls to opt out of global alert messaging
      if (!skipAlert) {
        this.globalModalService.alert({
          headerText: 'Error',
          bodyText: this.getErrorText(errorResponse)
        });
      }
    };

    const serverErrorJson = <ThErrorResponse>errorResponse.error;

    if (serverErrorJson?.redirect) {
      this.modalService.dismissAll();

      this.router.navigate([serverErrorJson.redirect]).finally(() => {
        // Showing the alert AFTER the redirect finishes to prevent weirdness
        // with table width recalculation
        showAlert();
      });
    } else {
      showAlert();
    }

    // Just rethrow the error so any subscribers can react to it
    return throwError(errorResponse);
  }

  private getErrorText(errorResponse: any): string {
    if (errorResponse && isObject(errorResponse.error)) {
      const serverErrorJson = errorResponse.error as ThErrorResponse;

      return serverErrorJson.message || 'An unexpected error occurred. Please try again later.';
    } else if (isString(errorResponse)) {
      return errorResponse;
    } else if (errorResponse instanceof HttpErrorResponse) {
      return errorResponse.message;
    } else {
      return String(errorResponse);
    }
  }

  // Copied from https://www.learnrxjs.io/operators/error_handling/retrywhen.html
  // and customized a bit for our needs
  private genericRetryStrategy({
    maxRetryAttempts = 3,
    scalingDuration = 100,
    excludedStatusCodes = [0, 302, 401, 403, 404, 504]
  }: {
    maxRetryAttempts?: number;
    scalingDuration?: number;
    excludedStatusCodes?: number[];
  } = {}): (attempts: Observable<any>) => Observable<number> {
    return (attempts: Observable<any>) =>
      attempts.pipe(
        mergeMap((error, i) => {
          const retryAttempt = i + 1;

          // if maximum number of retries have been met
          // or response is a status code we don't wish to retry
          // or it isn't a Redis exception, throw error
          if (
            retryAttempt > maxRetryAttempts ||
            excludedStatusCodes.find((e) => e === error.status) ||
            !this.isRedisException(error)
          ) {
            return throwError(error);
          }

          console.log(`Attempt ${retryAttempt}: retrying in ${retryAttempt * scalingDuration}ms for URL ${error.url}`);

          // retry after 1s, 2s, etc...
          return timer(retryAttempt * scalingDuration);
        })
      );
  }

  private isRedisException(errorResponse: any): boolean {
    if (errorResponse && isObject(errorResponse.error)) {
      const serverErrorJson = errorResponse.error as ThErrorResponse;

      return serverErrorJson.type === REDIS_EXCEPTION;
    } else if (errorResponse && isString(errorResponse.error)) {
      const serverErrorMessage = errorResponse.error as string;

      return serverErrorMessage.indexOf(REDIS_EXCEPTION) >= 0;
    }

    return false;
  }
}
